import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import {
  formatNumber,
  NumberFormatPreset,
  QuickViewReferenceValue,
  QuickViewWidget,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { useDateRange } from '../../components/DateRangeProvider';
import { I18nNamespace } from '~/common/types';
import { SpendType } from '~/common/types/cubes/SpendType';
import { useResponsibleUsersFilter } from '~/components/ResponsibleUsersProvider';
import { useSpendTypeFilter } from '~/hooks';
import { convertDateRangeToTimeDimension, getSegments } from '~/util/cube';

const formatValueCompact = formatNumber(NumberFormatPreset.CURRENCY_COMPACT);
const formatValue = formatNumber(NumberFormatPreset.CURRENCY);
const formatRate = formatNumber(NumberFormatPreset.PERCENT_DECIMAL);

type AverageNegotiationSavingWidgetProps = {
  filters?: Filter[];
  spendType: SpendType;
};
export const CUBE = 'AverageNegotiationSavings';
export const AverageNegotiationSavingsWidget = ({ filters, spendType }: AverageNegotiationSavingWidgetProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiationsAverageSavingWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'negotiations-average-saving-widget' });

  const { currentDateRange } = useDateRange();
  const responsibleUsersFilter = useResponsibleUsersFilter(`${CUBE}.responsible_uuid`);

  const spendTypeFilter = useSpendTypeFilter({ spendType, cubeTotalVolumeField: `${CUBE}.quote_total` });

  const queryValue = useCubeQuery({
    measures: [`${CUBE}.average_saving`, `${CUBE}.average_saving_rate`],
    timeDimensions: convertDateRangeToTimeDimension(`${CUBE}.date`, currentDateRange),
    filters: [...(filters ?? []), responsibleUsersFilter, ...spendTypeFilter],
  });

  const { totalValue: averageSavingsValue } = getSegments(queryValue);
  const { totalValue: averageSavingsRate } = getSegments(queryValue, 1);

  return (
    <QuickViewWidget
      ref={widgetRef}
      active={active}
      loading={queryValue.isLoading}
      title={t(`title`)}
      subTitle={t('subTitle')}
      value={formatValueCompact(averageSavingsValue)}
      valueTooltip={formatValue(averageSavingsValue)}
      data-intercom-target="NegotiationsAverageSavingWidget"
      referenceValue={<QuickViewReferenceValue description={t('description')} value={formatRate(averageSavingsRate)} />}
    />
  );
};
